import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { ButtonCustom } from 'shared/ui/button-custom';
import { Loader } from 'shared/ui/loader';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './draft-view.module.scss';

interface DraftApplication {
  id: number;
  documentId: string;
  bio?: string;
  email?: string;
  phone?: string;
  inn?: string;
  quantity?: string;
  car_type?: string;
  car_subtype?: string;
  car_brand?: string;
  vehicle?: string;
  pay_method?: string;
  condition?: string;
  car_params?: string;
  revenue?: string;
  // Поля для лизинговых заявок
  price?: string;
  initial_payment?: string;
  lease_period?: string;
  payment_type?: string;
  // Прочие поля
  car_type_other?: string;
  car_subtype_other?: string;
  car_brand_other?: string;
  car_model_other?: string;
  custom_uid?: string;
  createdAt: string;
  updatedAt: string;
  fulfilled: boolean;
}

export const DraftView = () => {
  const { type, uid } = useParams<{ type: 'select' | 'leasing'; uid: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<DraftApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getDraft = async () => {
    if (!type || !uid) return;

    try {
      setLoading(true);
      const endpoint =
        type === 'select' ? '/select-applications/app' : '/leasing-applications/app';

      const response = await apiClient.post(endpoint, { id: uid });
      setDraft(response.data.data);
    } catch (error) {
      console.error('Ошибка при загрузке черновика:', error);
      // Если черновик не найден, перенаправляем назад
      navigate('/dashboard/drafts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDraft();
  }, [type, uid]);

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const deleteDraft = async () => {
    if (!draft || !type) return;

    try {
      const endpoint =
        type === 'select'
          ? `/select-applications/${draft.id}`
          : `/leasing-applications/${draft.id}`;

      await apiClient.delete(endpoint);
      setShowDeleteDialog(false);
      navigate('/dashboard/drafts');
    } catch (error) {
      console.error('Ошибка при удалении черновика:', error);
    }
  };

  const handleContinue = () => {
    if (type === 'select') {
      navigate('/dashboard/selection-calc');
    } else {
      navigate('/dashboard/leasing-calc');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDraftCompleteness = () => {
    if (!draft) return 0;

    let requiredFields: string[];

    if (type === 'select') {
      requiredFields = ['bio', 'email', 'phone', 'inn', 'quantity', 'car_type', 'pay_method'];
    } else {
      requiredFields = [
        'bio',
        'email',
        'phone',
        'inn',
        'quantity',
        'car_type',
        'price',
        'initial_payment',
        'lease_period',
      ];
    }

    const filledFields = requiredFields.filter((field) => draft[field as keyof DraftApplication]);
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };

  const parseCarParams = (carParamsString?: string) => {
    if (!carParamsString) return [];

    try {
      const params = JSON.parse(carParamsString);
      if (Array.isArray(params)) {
        return params;
      }
    } catch (e) {
      console.error('Ошибка при парсинге car_params:', e);
    }
    return [];
  };

  if (loading) {
    return <Loader />;
  }

  if (!draft) {
    return (
      <Box className={styles.container}>
        <DashboardHeaderBackLink link={'/dashboard/drafts'} title={'Черновик не найден'} />
        <Box style={{ textAlign: 'center', marginTop: '50px' }}>
          <p>Черновик не найден или был удален</p>
        </Box>
      </Box>
    );
  }

  const completeness = getDraftCompleteness();
  const carParams = parseCarParams(draft.car_params);

  return (
    <Box className={styles.container}>
      <DashboardHeaderBackLink
        link={'/dashboard/drafts'}
        title={`Черновик заявки #${draft.documentId || draft.id}`}
      />

      <Box>
        <Card className={styles.card}>
          <CardHeader
            title={
              <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span>
                  {type === 'select' ? 'Заявка на подбор' : 'Заявка на лизинг'}#
                  {draft.documentId || draft.id}
                </span>
                <Chip
                  label={`${completeness}% заполнено`}
                  color={completeness > 70 ? 'success' : completeness > 40 ? 'warning' : 'error'}
                  variant='outlined'
                  size='small'
                />
              </Box>
            }
            action={
              <Box style={{ display: 'flex', gap: '10px' }}>
                <ButtonCustom onClick={handleContinue} className={styles.greenBtn}>
                  Продолжить редактирование
                </ButtonCustom>
                <ButtonCustom onClick={handleDelete} className={styles.redBtn}>
                  Удалить черновик
                </ButtonCustom>
              </Box>
            }
          />
          <CardContent className={styles.cardContent}>
            <List>
              {/* Основная информация */}
              <ListItem className={styles.sectionHeader}>
                <b>Основная информация</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>ФИО:</b>
                <span>{draft.bio || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Email:</b>
                <span>{draft.email || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Телефон:</b>
                <span>{draft.phone || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>ИНН:</b>
                <span>{draft.inn || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Количество ТС:</b>
                <span>{draft.quantity || '-'}</span>
              </ListItem>

              {draft.revenue && (
                <ListItem className={styles.listItem}>
                  <b>Выручка в год:</b>
                  <span>{draft.revenue}</span>
                </ListItem>
              )}

              {/* Информация о транспорте */}
              <ListItem className={styles.sectionHeader}>
                <b>Информация о транспортном средстве</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Тип ТС:</b>
                <span>{draft.car_type_other || draft.car_type || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Подтип ТС:</b>
                <span>{draft.car_subtype_other || draft.car_subtype || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Бренд:</b>
                <span>{draft.car_brand_other || draft.car_brand || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Модель:</b>
                <span>{draft.car_model_other || draft.vehicle || '-'}</span>
              </ListItem>

              {/* Параметры автомобиля */}
              {carParams.length > 0 && (
                <>
                  <ListItem className={styles.sectionHeader}>
                    <b>Параметры автомобиля</b>
                  </ListItem>
                  {carParams.map((param: any, index: number) => (
                    <ListItem key={index} className={styles.listItem}>
                      <b>{param.fieldName}:</b>
                      <span>{param.fieldValue || '-'}</span>
                    </ListItem>
                  ))}
                </>
              )}

              {/* Специфичные поля для подбора */}
              {type === 'select' && (
                <>
                  <ListItem className={styles.sectionHeader}>
                    <b>Условия подбора</b>
                  </ListItem>

                  {draft.condition && (
                    <ListItem className={styles.listItem}>
                      <b>Состояние:</b>
                      <span>{draft.condition}</span>
                    </ListItem>
                  )}

                  {draft.pay_method && (
                    <ListItem className={styles.listItem}>
                      <b>Способ покупки:</b>
                      <span>{draft.pay_method}</span>
                    </ListItem>
                  )}
                </>
              )}

              {/* Специфичные поля для лизинга */}
              {type === 'leasing' && (
                <>
                  <ListItem className={styles.sectionHeader}>
                    <b>Условия лизинга</b>
                  </ListItem>

                  {draft.price && (
                    <ListItem className={styles.listItem}>
                      <b>Стоимость 1 ед. предмета лизинга:</b>
                      <span>{draft.price} ₽</span>
                    </ListItem>
                  )}

                  {draft.initial_payment && (
                    <ListItem className={styles.listItem}>
                      <b>Первоначальный взнос:</b>
                      <span>{draft.initial_payment}%</span>
                    </ListItem>
                  )}

                  {draft.lease_period && (
                    <ListItem className={styles.listItem}>
                      <b>Срок лизинга:</b>
                      <span>{draft.lease_period} мес.</span>
                    </ListItem>
                  )}

                  {draft.payment_type && (
                    <ListItem className={styles.listItem}>
                      <b>График платежей:</b>
                      <span>{draft.payment_type}</span>
                    </ListItem>
                  )}
                </>
              )}

              {/* Системная информация */}
              <ListItem className={styles.sectionHeader}>
                <b>Системная информация</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Создан:</b>
                <span>{formatDate(draft.createdAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Последнее изменение:</b>
                <span>{formatDate(draft.updatedAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Уникальный ID:</b>
                <span>{draft.custom_uid || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Статус:</b>
                <span>
                  <Chip
                    label={draft.fulfilled ? 'Завершен' : 'Черновик'}
                    color={draft.fulfilled ? 'success' : 'warning'}
                    variant='outlined'
                    size='small'
                  />
                </span>
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='delete-dialog-title'>
          <span>Подтверждение удаления</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить черновик заявки #{draft.documentId || draft.id}? Это
            действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom
            className={styles.modalButtonCancel}
            onClick={() => setShowDeleteDialog(false)}
          >
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.redBtn} onClick={deleteDraft}>
            Удалить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
